"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Philosophy() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tenets = [{
    index: "01",
    declaration: "هر فضا، روایتی منحصر به فرد از زندگی است."
  }, {
    index: "02",
    declaration: "تعادل میان فرم و عملکرد، جوهره طراحی ماست."
  }, {
    index: "03",
    declaration: "جزئیات، تفاوت میان یک فضای معمولی و یک اثر هنری است."
  }, {
    index: "04",
    declaration: "نور، روحِ جاری در کالبد معماری داخلی است."
  }];
  return <section data-section-id="2008" id="فلسفه-طراحی-ما" className="bg-background text-foreground py-32 md:py-44 px-6 md:px-12 lg:px-16 overflow-hidden">
      <div className="max-w-[1480px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-6 relative transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div aria-hidden="true" className="pointer-events-none absolute -top-8 -left-4 select-none font-mono font-black leading-none" style={{
            fontSize: "clamp(12rem, 22vw, 22rem)",
            color: "transparent",
            WebkitTextStroke: "1px hsl(220 15% 8% / 0.06)",
            opacity: 0.06,
            letterSpacing: "-0.04em"
          }}>
              00
            </div>

            {}
            <div className="relative font-mono uppercase mb-10" style={{
            letterSpacing: "0.18em",
            fontSize: "clamp(0.6875rem, 0.8vw, 0.8125rem)"
          }}>
              <span className="text-primary">§ 00 — استودیو آرتین / بیانیه</span>
            </div>

            {}
            <h1 className="relative font-default font-light" style={{
            fontSize: "clamp(3.25rem, 8vw, 7.5rem)",
            lineHeight: 0.88,
            letterSpacing: "-0.04em"
          }}>
              ما فضا را{" "}
              <span className="font-serif italic font-normal">
                نمی‌سازیم
              </span>{" "}
              ، آن را خلق می‌کنیم.
            </h1>

            {}
            <div className="relative mt-12 mb-10 flex items-center">
              <div className="flex-1 h-px bg-border" />
              <div className="w-1.5 h-1.5 ml-0 bg-primary" />
            </div>

            {}
            <p className="font-default relative" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
            lineHeight: 1.55
          }}>
              آرتین صرفاً یک استودیوی طراحی نیست. ما معماران اتمسفر هستیم که با ترکیب دقیق نور، متریال و هنر، فضاهایی را طراحی می‌کنیم که بازتاب‌دهنده شخصیت و سبک زندگی شماست. هر پروژه برای ما یک بوم سفید است که با دقت و وسواس، داستانی از آرامش و زیبایی را بر روی آن ترسیم می‌کنیم. ما به دنبال مد نیستیم؛ ما به دنبال خلق فضاهایی هستیم که در گذر زمان، اصالت خود را حفظ کنند.
            </p>

            {}
            <div className="relative mt-14">
              {tenets.map((tenet, i) => <div key={tenet.index} data-index={i} className={`transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: leftVisible ? `${200 + i * 80}ms` : "0ms"
            }}>
                  <div className="flex items-center pt-5 pb-5">
                    <div className="font-mono shrink-0 w-16 md:w-20 text-primary" style={{
                  fontSize: "clamp(0.6875rem, 0.8vw, 0.8125rem)",
                  letterSpacing: "0.18em"
                }}>
                      {tenet.index} —
                    </div>
                    <div className="font-serif italic" style={{
                  fontSize: "clamp(1.0625rem, 1.4vw, 1.375rem)",
                  lineHeight: 1.3
                }}>
                      {tenet.declaration}
                    </div>
                  </div>
                  {}
                  <div className="flex items-center">
                    <div className="flex-1 h-px bg-border/50" />
                    <div className="w-1.5 h-1.5 bg-primary" />
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-6 transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: rightVisible ? "150ms" : "0ms"
        }}>
            <div className="relative w-full h-full min-h-[480px] lg:min-h-[720px]">
              {}
              <div className="relative w-full h-full aspect-[4/5] lg:aspect-auto lg:absolute lg:inset-0 overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705125/img/interior_design_concept.webp" alt="طراحی داخلی مدرن با جزئیات دقیق و نورپردازی هوشمند" loading="lazy" decoding="async" className="w-full h-full object-cover" />

                {}
                <div className="absolute top-4 left-4 w-3 h-3 pointer-events-none">
                  <div className="absolute top-0 left-0 w-full h-px bg-primary" />
                  <div className="absolute top-0 left-0 h-full w-px bg-primary" />
                </div>
                {}
                <div className="absolute top-4 right-4 w-3 h-3 pointer-events-none">
                  <div className="absolute top-0 right-0 w-full h-px bg-primary" />
                  <div className="absolute top-0 right-0 h-full w-px bg-primary" />
                </div>
                {}
                <div className="absolute bottom-4 left-4 w-3 h-3 pointer-events-none">
                  <div className="absolute bottom-0 left-0 w-full h-px bg-primary" />
                  <div className="absolute bottom-0 left-0 h-full w-px bg-primary" />
                </div>
                {}
                <div className="absolute bottom-4 right-4 w-3 h-3 pointer-events-none">
                  <div className="absolute bottom-0 right-0 w-full h-px bg-primary" />
                  <div className="absolute bottom-0 right-0 h-full w-px bg-primary" />
                </div>

                {}
                <div className="absolute bottom-0 left-0 right-0 px-6 py-4 font-mono uppercase bg-background/85 backdrop-blur-sm border-t border-primary/40" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.18em"
              }}>
                  FIG. 01 — آرتین / طراح ارشد / استودیو
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}